/**
 * Slim copies of the workflow data the portals read for every client (Open
 * Task / Assigned columns, workflow filters and colours):
 *
 *   openWorkflowTaskRows/{clientId}__{taskId}   every open task, without its
 *     notes, checklist and completion detail (Oct 2026: 1,213 open tasks were
 *     15.5 MB, 12 MB of it notes with pasted screenshots);
 *   activeWorkflowRunRows/{clientId}__{runId}   every active run, without its
 *     template copy except the approval document types the Annual Review /
 *     New Documents checks read (src/utils/annualReviewRun.js) — 1,195 runs
 *     were 10.9 MB, 9.9 MB of it template copies.
 *
 * The server keeps them in step (server/lib/workflowRowsMirror.cjs); the
 * browser only reads them (src/services/workflowFirestoreService.js). A row is
 * never a stand-in for the task or run: the task window loads the live task.
 *
 * CommonJS body so Node 18 can require() it and webpack can bundle it
 * (see src/utils/clientListBucket.js). Keep it free of object rest/spread.
 */

const OPEN_TASK_ROWS_COLLECTION = 'openWorkflowTaskRows';
const ACTIVE_RUN_ROWS_COLLECTION = 'activeWorkflowRunRows';
const OPEN_TASK_ROW_OMIT = Object.freeze(['taskNotes', 'checklist', 'completion']);

function workflowRowId(clientId, docId) {
  return `${clientId}__${docId}`;
}

function copyWithout(source, omit) {
  const out = {};
  Object.keys(source || {}).forEach((key) => {
    if (omit.indexOf(key) === -1 && source[key] !== undefined) out[key] = source[key];
  });
  return out;
}

/** Row for an open task (`clientId` / `taskId` from the task's path). */
function buildOpenTaskRow(task, ids) {
  const row = copyWithout(task, OPEN_TASK_ROW_OMIT);
  row.clientId = ids.clientId;
  row.taskId = ids.taskId;
  return row;
}

/** Row for an active run: no template copy beyond each task's approval document type. */
function buildActiveRunRow(run, ids) {
  const row = copyWithout(run, ['templateSnapshot']);
  const snapshotTasks = run && run.templateSnapshot && Array.isArray(run.templateSnapshot.tasks)
    ? run.templateSnapshot.tasks
    : [];
  const approvalTasks = [];
  snapshotTasks.forEach((task) => {
    const documentType = task && task.completion && task.completion.approval && task.completion.approval.documentType;
    if (documentType) approvalTasks.push({ completion: { approval: { documentType } } });
  });
  if (approvalTasks.length) row.templateSnapshot = { tasks: approvalTasks };
  row.clientId = ids.clientId;
  row.runId = ids.runId;
  return row;
}

const WORKFLOW_ROW_MARK = '__workflowListRow';

/** Mark a delivered row object (browser). Non-enumerable: lost on spread, never written back. */
function markWorkflowListRow(item) {
  if (item && typeof item === 'object') Object.defineProperty(item, WORKFLOW_ROW_MARK, { value: true });
  return item;
}

function isWorkflowListRow(item) {
  return Boolean(item && item[WORKFLOW_ROW_MARK]);
}

module.exports = {
  OPEN_TASK_ROWS_COLLECTION,
  ACTIVE_RUN_ROWS_COLLECTION,
  OPEN_TASK_ROW_OMIT,
  workflowRowId,
  buildOpenTaskRow,
  buildActiveRunRow,
  markWorkflowListRow,
  isWorkflowListRow,
};
